import {
  isInvalidString,
  isInvalidPurchaseOrderNumber,
  isInvalidQuantity,
  isInvalidProductBarcode,
  isInvalidProductName,
  isInvalidCancellationReason
} from "src/validation/stringValidation";
import { isInvalidISODate } from "src/helpers/dateHelper";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { AmendPurchaseOrderFormDataKey, ProductAmendmentInputField } from "src/types";

class AmendPurchaseOrderFormValidator extends FormValidator {
  private static validatePurchaseOrderNumber(value: unknown): ValidationError<"purchaseOrderId"> | undefined {
    return isInvalidPurchaseOrderNumber(value)
      ? new ValidationError("Enter a valid purchase order number (e.g., 'PO-2055')", "purchaseOrderId")
      : undefined;
  }

  private static validateEstDeliveryDate(value: unknown): ValidationError<"estDeliveryDate"> | undefined {
    return value && isInvalidISODate(value)
      ? new ValidationError("Enter a valid date (example format: '01/01/2026')", "estDeliveryDate")
      : undefined;
  }

  private static validateCancellationReason(value: unknown): ValidationError<"cancellationReason"> | undefined {
    return isInvalidCancellationReason(value?.toString())
      ? new ValidationError("Enter a valid cancellation reason", "cancellationReason")
      : undefined;
  }

  private static validateProductAmendments(value: unknown): ValidationError<"productAmendments"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return;
    }

    for (const arrayValue of value) {
      const { id, name, amendedQty, amendedUkQty, amendedEuQty, hasWarehouseSplit, barcode, size } =
        arrayValue as ProductAmendmentInputField;

      if (isInvalidProductName(name)) {
        return new ValidationError(`Invalid product name (${id})`, "productAmendments");
      }

      if (hasWarehouseSplit) {
        if (isInvalidQuantity(amendedUkQty, 0)) {
          return new ValidationError(`Invalid UK quantity (${id})`, "productAmendments");
        }
        if (isInvalidQuantity(amendedEuQty, 0)) {
          return new ValidationError(`Invalid EU quantity (${id})`, "productAmendments");
        }
        const totalQty = (Number(amendedUkQty) || 0) + (Number(amendedEuQty) || 0);
        if (totalQty < 1) {
          return new ValidationError(`Total quantity must be at least 1 (${id})`, "productAmendments");
        }
      } else {
        if (isInvalidQuantity(amendedQty)) {
          return new ValidationError(`Invalid product quantity (${id})`, "productAmendments");
        }
      }

      if (size && isInvalidString(size, 0, 25)) {
        return new ValidationError(`Invalid product size (${id})`, "productAmendments");
      }

      if (isInvalidProductBarcode(barcode)) {
        return new ValidationError(`Invalid product barcode (${id})`, "productAmendments");
      }
    }

    return undefined;
  }

  private static readonly validationStrategies: Record<
    AmendPurchaseOrderFormDataKey,
    (inputValue: unknown) => ValidationError<AmendPurchaseOrderFormDataKey> | undefined
  > = {
    purchaseOrderId: AmendPurchaseOrderFormValidator.validatePurchaseOrderNumber,
    productAmendments: AmendPurchaseOrderFormValidator.validateProductAmendments,
    estDeliveryDate: AmendPurchaseOrderFormValidator.validateEstDeliveryDate,
    cancellationReason: AmendPurchaseOrderFormValidator.validateCancellationReason
  };

  static validatePurchaseOrderFormData<T extends AmendPurchaseOrderFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(
      formData,
      AmendPurchaseOrderFormValidator.validationStrategies
    ) as ValidationErrors<T>;
  }
}

export { AmendPurchaseOrderFormValidator };
